import { useMutation, useQuery } from '@apollo/client';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Accordion, Col } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';

import {
  GET_ADMIN_TOURNAMENT_LIST,
  GET_USER_TOURNAMENT_STATE,
  RESET_USER_TOURNAMENT,
  UPDATE_USER_TOURNAMENT_SCORE,
} from '../../../graphql';

type Props = {
  userId: string;
};

type TournamentOption = { id: string; name: string; startDate: string; endDate: string };
type Milestone = { id: string; score: number; isClaimed: boolean };

/** What the BE should clear. Everything omitted defaults to true there, so always be explicit. */
type ResetScope = {
  milestoneIds?: string[];
  resetClaimedMilestones: boolean;
  resetRankGroupReward: boolean;
  resetCurrentRound: boolean;
};

const TOURNAMENT_LIST_LIMIT = 40;

const ClaimedIcon = ({ claimed }: { claimed: boolean }) => (
  <i
    className={claimed ? 'fas fa-check-square' : 'fas fa-times-circle'}
    style={{ color: claimed ? '#2bf702' : '#FF0000' }}
  />
);

const formatDate = (value: string) => (value ? new Date(value).toISOString().slice(0, 10) : '?');

export function UserTournamentDisplay({ userId }: Props): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [tournamentId, setTournamentId] = useState('');
  const [score, setScore] = useState('0');
  const [resetError, setResetError] = useState<string | null>(null);
  const [isRefetching, setIsRefetching] = useState(false);

  // The list is ordered by date DESC, so the first entry is the most recent tournament.
  const { data: listData, loading: listLoading } = useQuery(GET_ADMIN_TOURNAMENT_LIST, {
    variables: { limit: TOURNAMENT_LIST_LIMIT, offset: 0 },
    skip: !expanded,
  });
  const tournaments: TournamentOption[] = useMemo(
    () => listData?.liveOps?.tournaments?.list ?? [],
    [listData]
  );

  const {
    data: stateData,
    loading: stateLoading,
    refetch,
  } = useQuery(GET_USER_TOURNAMENT_STATE, {
    variables: { userId, tournamentId },
    // Resolving the milestone ladder is not free, and this renders on every user lookup.
    skip: !expanded || !userId || !tournamentId,
    fetchPolicy: 'network-only',
  });

  const [updateScore, { loading: updateLoading }] = useMutation(UPDATE_USER_TOURNAMENT_SCORE);
  const [resetUserTournament, { loading: resetLoading }] = useMutation(RESET_USER_TOURNAMENT);

  const tournament = stateData?.user?.adminTournament;
  const milestones: Milestone[] = tournament?.milestones ?? [];
  const hasClaimedMilestone = milestones.some(({ isClaimed }) => isClaimed);
  // currentRound is null exactly when the user has no TournamentUser row, and that row can only
  // be created by actually playing a round - its NOT NULL round columns have no sane default.
  const hasParticipated = Boolean(tournament?.currentRound);

  // Preselect the newest tournament once the list arrives, without fighting a manual choice.
  useEffect(() => {
    if (!tournamentId && tournaments.length > 0) {
      setTournamentId(tournaments[0].id);
    }
  }, [tournaments, tournamentId]);

  useEffect(() => {
    setScore(String(tournament?.meScore ?? 0));
  }, [tournament]);

  // refetch() always hits the network; a repeat useLazyQuery call with unchanged variables does not.
  const refreshState = useCallback(() => {
    setIsRefetching(true);
    return refetch().finally(() => setIsRefetching(false));
  }, [refetch]);

  const busy = listLoading || stateLoading || updateLoading || resetLoading || isRefetching;

  const handleSaveScore = useCallback(async () => {
    if (!tournamentId) {
      return;
    }
    setResetError(null);
    try {
      await updateScore({ variables: { input: { tournamentId, userId, score: Number(score) || 0 } } });
      await refreshState();
    } catch (error) {
      setResetError((error as Error).message);
    }
  }, [updateScore, refreshState, tournamentId, userId, score]);

  const handleReset = useCallback(
    async (label: string, scope: ResetScope) => {
      if (!tournamentId) {
        return;
      }
      if (
        !window.confirm(
          `${label} on this tournament for this user? Items and currency already granted are NOT taken back.`
        )
      ) {
        return;
      }
      setResetError(null);
      try {
        await resetUserTournament({ variables: { input: { tournamentId, userId, ...scope } } });
        await refreshState();
      } catch (error) {
        setResetError((error as Error).message);
      }
    },
    [resetUserTournament, refreshState, tournamentId, userId]
  );

  const unclaimMilestone = useCallback(
    (milestone: Milestone) =>
      handleReset(`Un-claim the ${milestone.score} pts milestone`, {
        milestoneIds: [milestone.id],
        resetClaimedMilestones: true,
        resetRankGroupReward: false,
        resetCurrentRound: false,
      }),
    [handleReset]
  );

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
              User Tournament
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <>
              {resetError && (
                <Alert variant="danger" className="py-2">
                  {resetError}
                </Alert>
              )}
              <Form>
                <Form.Row className="align-items-end">
                  <Form.Group as={Col} xs={12} md={5} className="mb-2">
                    <Form.Label className="small text-muted mb-1">Tournament</Form.Label>
                    <Form.Control
                      as="select"
                      size="sm"
                      value={tournamentId}
                      disabled={busy || tournaments.length === 0}
                      onChange={(event) => setTournamentId(event.target.value)}
                    >
                      {tournaments.length === 0 && <option value="">{listLoading ? 'Loading…' : 'No tournament'}</option>}
                      {tournaments.map(({ id, name, startDate, endDate }) => (
                        <option key={id} value={id}>
                          {name} ({formatDate(startDate)} → {formatDate(endDate)})
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                  <Form.Group as={Col} xs={6} md={2} className="mb-2">
                    <Form.Label className="small text-muted mb-1">Score</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      value={score}
                      disabled={busy || !hasParticipated}
                      onChange={(event) => setScore(event.target.value)}
                    />
                  </Form.Group>
                  <Col xs="auto" className="mb-2">
                    <Button size="sm" variant="primary" disabled={busy || !hasParticipated} onClick={handleSaveScore}>
                      Save score
                    </Button>
                  </Col>
                </Form.Row>
              </Form>

              <hr className="my-2" />
              <div className="small text-muted text-uppercase mb-2">Reset test account</div>
              <div className="d-flex justify-content-start align-items-baseline flex-wrap mb-3" style={{ gap: 8 }}>
                <Button size="sm" disabled={busy || !tournamentId} onClick={refreshState}>
                  {stateLoading || isRefetching ? <Spinner animation="border" size="sm" /> : 'Refresh'}
                </Button>
                <Button
                  size="sm"
                  variant="warning"
                  disabled={busy || !hasClaimedMilestone}
                  onClick={() =>
                    handleReset('Un-claim every milestone', {
                      resetClaimedMilestones: true,
                      resetRankGroupReward: false,
                      resetCurrentRound: false,
                    })
                  }
                >
                  <i className="fas fa-box-open" /> Un-claim all milestones
                </Button>
                <Button
                  size="sm"
                  variant="warning"
                  disabled={busy || !hasParticipated}
                  onClick={() =>
                    handleReset('Un-claim the rank group reward and drop the frozen rank', {
                      resetClaimedMilestones: false,
                      resetRankGroupReward: true,
                      resetCurrentRound: false,
                    })
                  }
                >
                  <i className="fas fa-trophy" /> Reset rank group reward
                </Button>
                <Button
                  size="sm"
                  variant="warning"
                  disabled={busy || !hasParticipated}
                  onClick={() =>
                    handleReset('Put the current round back to READY_TO_PLAY', {
                      resetClaimedMilestones: false,
                      resetRankGroupReward: false,
                      resetCurrentRound: true,
                    })
                  }
                >
                  <i className="fas fa-redo" /> Reset current round
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  disabled={busy || !hasParticipated}
                  onClick={() =>
                    handleReset('Un-claim every reward AND replay the current round', {
                      resetClaimedMilestones: true,
                      resetRankGroupReward: true,
                      resetCurrentRound: true,
                    })
                  }
                >
                  <i className="fas fa-battery-empty" /> Full reset
                </Button>
              </div>

              {tournament && !hasParticipated && !busy && (
                <Alert variant="secondary" className="py-2">
                  This user has never played a round in this tournament, so there is nothing to change or reset. Have
                  the account play one round first.
                </Alert>
              )}
              {tournament && (
                <>
                  <div className="mb-2">
                    Total {tournament.meScore ?? 0} pts — rank group{' '}
                    {tournament.userRankGroup
                      ? `${tournament.userRankGroup.minRank}-${tournament.userRankGroup.maxRank}`
                      : 'none'}{' '}
                    — reward claimed <ClaimedIcon claimed={Boolean(tournament.isRankGroupRewardClaimed)} /> — round{' '}
                    {tournament.currentRound?.state ?? 'not started'}
                  </div>
                  {milestones.length > 0 && (
                    <Card style={{ marginBottom: 8, width: 'fit-content' }}>
                      <Card.Header className="text-center py-1">
                        <Card.Title className="mb-0 small text-uppercase">Milestones</Card.Title>
                      </Card.Header>
                      <Card.Body className="p-0">
                        <Table bordered responsive hover size="sm" className="mb-0">
                          <thead className="text-center">
                            <tr>
                              <th>Points</th>
                              <th>Claimed</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {milestones.map((milestone) => (
                              <tr key={milestone.id}>
                                <td className="text-center">{milestone.score}</td>
                                <td className="text-center">
                                  <ClaimedIcon claimed={milestone.isClaimed} />
                                </td>
                                <td className="text-center">
                                  <Button
                                    size="sm"
                                    variant="outline-warning"
                                    disabled={busy || !milestone.isClaimed}
                                    onClick={() => unclaimMilestone(milestone)}
                                  >
                                    Un-claim
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  )}
                </>
              )}
            </>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>
    </Card>
  );
}
